import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Flag, Microchip, Save } from "lucide-react";

import { api } from "@/api/client";
import { usePoolPlacements, usePoolResourceCatalog } from "@/api/local/pools";
import type { Pool, ResolvedPlacement, Service } from "@/api/types";
import { useConfirm } from "@/components/ConfirmDialog";
import { CpuPicker, GpuPicker } from "@/components/ResourceShapePicker";
import { Section } from "@/components/Section";
import { humanCloudLabel } from "@/workloads/tree-data";

type ResourceKind = "gpu" | "cpu";

/**
 * Derive the resource kind from a Pool row. A Pool may legitimately
 * have neither `accelerators` nor `cpus` set (cloud default shape) —
 * we default such rows to GPU since that's the dominant case for
 * inference workloads and matches the placeholder pool created by
 * the tree's "+ pool" button.
 */
function resourceKindOf(pool: Pool): ResourceKind {
  if (pool.cpus) return "cpu";
  return "gpu";
}

interface Props {
  pool: Pool;
  service: Service;
}

/**
 * Pool Spec — the resource shape ("what hardware").
 *
 * Pools no longer carry a per-pool (cloud, region) placement list —
 * the candidates SkyPilot's optimiser sees are derived from the
 * Provider config inheritance chain (Global / Service / Pool override).
 * Spot vs ondemand is similarly derived: each provider variant the
 * operator enables on the Providers tab dictates its own ``use_spot``
 * value, and the renderer enforces SkyPilot's all-or-nothing invariant
 * by failing the render when the resolved placements disagree.
 * The "where will replicas spawn?" preview at the top of this page
 * surfaces the resolved candidates via `usePoolPlacements`; to refine
 * them, edit Providers on this Pool's Providers tab.
 *
 * "What to run" (image / command / args / env / probe) lives on the
 * dedicated **Workload** tab — a pool either inherits the service-level
 * spec or owns its own (TTS-style: bf16 on H100/A100, int4 on L4) with
 * its own draft / promote cycle. There is no per-field override layer
 * any more; the toggle on that tab is the single source of truth.
 *
 * Capacity / cost knobs (`max_replicas`, `max_price_per_hour`) live on
 * the Autoscaling tab because they bound the autoscaler/schedule
 * outputs; Danger zone (detach) lives on Settings.
 */
/**
 * Pool fields that are "hard" — saving a PATCH that touches any of
 * these recreates the pool's SkyServe service (``sky.serve.down
 * --purge`` then a fresh ``up`` on the next reconcile tick). The
 * backend computes the same classification in
 * ``controller/api/routers/pools.py::classify_patch`` from the same
 * field names; if you change one side, change the other. See
 * ``docs/proposals/spec-change-orchestration.md`` for the design.
 *
 * "Soft" fields (``priority``, autoscaling knobs, display_name)
 * stay in-place and apply via SkyServe's BLUE_GREEN / ROLLING
 * update path — no downtime.
 */
const HARD_FIELDS = new Set([
  "accelerators",
  "cpus",
  "memory_gb",
  "disk_gb",
  "num_nodes",
]);

interface PendingPatch {
  priority: number;
  accelerators: string | null;
  cpus: string | null;
  memory_gb: string | null;
  num_nodes: number;
  disk_gb: number | null;
}

/**
 * Parse a CSV ``accelerators`` string (``"L4:1,L40S:1"``) into a
 * normalized set of ``"<name>:<count>"`` tokens. Whitespace, case,
 * and order are flattened so the comparator only looks at the actual
 * (GPU, count) pairs. Mirrors
 * ``controller.api.routers.pools._parse_accelerator_tokens``.
 */
function parseAcceleratorTokens(value: string | null): Set<string> {
  if (!value) return new Set();
  const out = new Set<string>();
  for (const raw of value.split(",")) {
    const token = raw.trim();
    if (!token || !token.includes(":")) continue;
    const [name, countStr] = token.split(":", 2);
    const trimmedName = name.trim().toLowerCase();
    const count = Number(countStr?.trim());
    if (trimmedName && Number.isInteger(count) && count > 0) {
      out.add(`${trimmedName}:${count}`);
    }
  }
  return out;
}

/**
 * Is the ``accelerators`` change a pure additive expansion of the
 * candidate set (e.g. ``"L4:1"`` → ``"L4:1,L40S:1"``)? Such a change
 * is downgraded from hard to soft on the backend — existing replicas
 * stay valid and SkyServe's BLUE_GREEN path absorbs the new option
 * without a service purge. Keep this in sync with
 * ``_accelerators_change_is_soft`` on the controller side.
 */
function isAcceleratorsSupersetExpansion(
  oldValue: string | null,
  newValue: string | null,
): boolean {
  const oldTokens = parseAcceleratorTokens(oldValue);
  if (oldTokens.size === 0) return false;
  const newTokens = parseAcceleratorTokens(newValue);
  if (newTokens.size <= oldTokens.size) return false;
  for (const t of oldTokens) {
    if (!newTokens.has(t)) return false;
  }
  return true;
}

function diffHardFields(pool: Pool, next: PendingPatch): string[] {
  const changed: string[] = [];
  const oldAccel = pool.accelerators ?? null;
  if (
    oldAccel !== next.accelerators &&
    !isAcceleratorsSupersetExpansion(oldAccel, next.accelerators)
  ) {
    changed.push("accelerators");
  }
  if ((pool.cpus ?? null) !== next.cpus) changed.push("cpus");
  if ((pool.memory_gb ?? null) !== next.memory_gb) {
    changed.push("memory_gb");
  }
  if (pool.num_nodes !== next.num_nodes) changed.push("num_nodes");
  if ((pool.disk_gb ?? null) !== next.disk_gb) changed.push("disk_gb");
  return changed.filter((f) => HARD_FIELDS.has(f));
}

export function PoolSpec({ pool, service }: Props) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [priority, setPriority] = useState(pool.priority);
  const [resourceKind, setResourceKind] = useState<ResourceKind>(
    () => resourceKindOf(pool),
  );
  const [accelerators, setAccelerators] = useState(pool.accelerators ?? "");
  const [cpus, setCpus] = useState(pool.cpus ?? "");
  const [memoryGb, setMemoryGb] = useState(pool.memory_gb ?? "");
  const [numNodes, setNumNodes] = useState(pool.num_nodes);
  const [diskGb, setDiskGb] = useState(
    pool.disk_gb == null ? "" : String(pool.disk_gb),
  );

  const resolvedPlacements = usePoolPlacements(pool.id);
  const resourceCatalogQuery = usePoolResourceCatalog(pool.id);

  useEffect(() => {
    setPriority(pool.priority);
    setResourceKind(resourceKindOf(pool));
    setAccelerators(pool.accelerators ?? "");
    setCpus(pool.cpus ?? "");
    setMemoryGb(pool.memory_gb ?? "");
    setNumNodes(pool.num_nodes);
    setDiskGb(pool.disk_gb == null ? "" : String(pool.disk_gb));
  }, [pool.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function buildPatch(): PendingPatch | Error {
    const trimmedAcc = accelerators.trim();
    if (
      resourceKind === "gpu" &&
      trimmedAcc !== "" &&
      !trimmedAcc.includes(":")
    ) {
      return new Error(
        "accelerators must be in '<name>:<count>' form (e.g. 'H100:1')",
      );
    }
    const parsedDisk = diskGb.trim() === "" ? null : Number(diskGb);
    if (
      parsedDisk !== null &&
      (!Number.isFinite(parsedDisk) || parsedDisk < 1)
    ) {
      return new Error("disk (GiB) must be a positive integer or blank");
    }
    return {
      priority,
      // Switching resource kind: the inactive kind's columns are
      // explicitly NULL'd so SkyPilot doesn't end up with both
      // `accelerators:` and `cpus:` set after a flip.
      accelerators:
        resourceKind === "gpu" && trimmedAcc !== "" ? trimmedAcc : null,
      cpus:
        resourceKind === "cpu" && cpus.trim() !== "" ? cpus.trim() : null,
      memory_gb:
        resourceKind === "cpu" && memoryGb.trim() !== ""
          ? memoryGb.trim()
          : null,
      num_nodes: numNodes,
      disk_gb: parsedDisk,
    };
  }

  const [validationError, setValidationError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (patch: PendingPatch) =>
      api.patch<Pool>(`/pools/${pool.id}`, patch),
    onSuccess: () => {
      setValidationError(null);
      queryClient.invalidateQueries({ queryKey: ["pools", "all"] });
      queryClient.invalidateQueries({
        queryKey: ["service", pool.service_id, "pools"],
      });
      queryClient.invalidateQueries({
        queryKey: ["pools", pool.id, "placements"],
      });
    },
  });

  async function handleSave() {
    const built = buildPatch();
    if (built instanceof Error) {
      setValidationError(built.message);
      return;
    }
    setValidationError(null);
    const changedHard = diffHardFields(pool, built);
    if (changedHard.length > 0) {
      // Phase 5 of spec-change-orchestration: a hard-spec change
      // triggers ``sky.serve.down --purge`` then a fresh ``up`` on
      // the next reconcile tick, which means **brief downtime**.
      // Surface this explicitly so the operator doesn't ship a
      // resource-shape change during peak hours by accident.
      const ok = await confirm({
        title: "Recreate SkyServe service?",
        tone: "warn",
        confirmLabel: "Save and recreate",
        cancelLabel: "Cancel",
        description: (
          <>
            <p style={{ marginTop: 0 }}>
              You changed the pool&rsquo;s resource shape (
              <code>{changedHard.join(", ")}</code>). SkyPilot can&rsquo;t
              swap a replica&rsquo;s GPU / CPU / disk shape in place, so
              backstage will tear down the current SkyServe service for
              this pool (<code>sky serve down --purge</code>) and launch a
              fresh one with the new shape on the next reconcile tick.
            </p>
            <p>
              Expect <strong>a brief downtime</strong> (typically tens of
              seconds to a few minutes) while the new replicas come up.
              If you want zero-downtime resource changes, create a new
              pool with the target shape, wait for it to be serving,
              then scale this one to zero.
            </p>
            <p style={{ marginBottom: 0 }} className="muted">
              Soft fields (priority, autoscaling, display name) save
              without downtime — only hard fields trigger this dialog.
            </p>
          </>
        ),
      });
      if (!ok) return;
    }
    save.mutate(built);
  }

  const reservedHint =
    pool.reservation_id != null
      ? `Backed by reservation #${pool.reservation_id}. Set this pool's priority to 0 and pin schedules.min_replicas to drain the reservation first — see Schedules tab.`
      : null;

  return (
    <Section
      title="Pool spec"
      icon={<Microchip />}
      actions={
        <button
          className="btn btn--primary"
          onClick={() => void handleSave()}
          disabled={save.isPending}
        >
          <Save />
          save
        </button>
      }
    >
      <div className="kv">
        <span className="k">placements (resolved)</span>
        <span className="v mono">{describePlacements(resolvedPlacements)}</span>
        <span className="k">resource</span>
        <span className="v mono">
          {describeResource({
            kind: resourceKind,
            accelerators,
            cpus,
            memoryGb,
            numNodes,
          })}
          {isResourceDirty(pool, {
            kind: resourceKind,
            accelerators,
            cpus,
            memoryGb,
            numNodes,
          }) && (
            <em className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
              — unsaved
            </em>
          )}
        </span>
        <span className="k">sky_service_name</span>
        <span className="v mono">{pool.sky_service_name}</span>
        <span className="k">workload mode</span>
        <span className="v mono">
          {pool.workload != null || pool.workload_draft != null ? (
            <>
              own{" "}
              <em className="muted" style={{ fontSize: 11 }}>
                — see Workload tab
              </em>
            </>
          ) : (
            <>
              inherit{" "}
              <em className="muted" style={{ fontSize: 11 }}>
                · {service.name}
              </em>
            </>
          )}
        </span>
      </div>

      {reservedHint && (
        <div className="flash" style={{ marginTop: 16 }}>
          <Flag
            style={{
              width: 14,
              height: 14,
              verticalAlign: "middle",
              marginRight: 6,
            }}
          />
          {reservedHint}
        </div>
      )}

      <div className="form-grid" style={{ marginTop: 16 }}>
        <label>priority</label>
        <div className="field">
          <input
            type="number"
            value={priority}
            onChange={(e) => setPriority(Number(e.target.value))}
          />
          <div className="field-hint">
            Lower wins (0 = highest). Envoy serves traffic top-down: P0
            soaks load until it's saturated, then the autoscaler bleeds
            weight onto P1, P2, … Reserved pools should be priority 0
            so the planner consumes them first.
          </div>
        </div>
        <label>resource kind</label>
        <div className="field">
          <select
            value={resourceKind}
            onChange={(e) =>
              setResourceKind(e.target.value as ResourceKind)
            }
          >
            <option value="gpu">GPU (accelerators)</option>
            <option value="cpu">CPU only</option>
          </select>
          <div className="field-hint">
            Forwarded to SkyPilot&rsquo;s <code>resources:</code> block as
            either <code>accelerators:</code> or <code>cpus:</code>. The
            other side&rsquo;s columns are cleared on save.
          </div>
        </div>
        {resourceKind === "gpu" && (
          <>
            <label>accelerators</label>
            <div className="field">
              <GpuPicker
                value={accelerators}
                onChange={setAccelerators}
                catalog={resourceCatalogQuery.data}
                isLoading={resourceCatalogQuery.isLoading}
              />
              <div className="field-hint">
                Pick a GPU and count. Solid chips are providers
                enabled on this pool&rsquo;s inheritance chain; dashed
                chips are catalog-available but not enabled — turn
                them on in the <strong>Providers</strong> tab to use.
                Leave blank to let SkyPilot pick the cloud&rsquo;s
                default shape.
              </div>
            </div>
          </>
        )}
        {resourceKind === "cpu" && (
          <>
            <label>cpu shape</label>
            <div className="field">
              <CpuPicker
                cpus={cpus}
                memoryGb={memoryGb}
                onChange={(next) => {
                  setCpus(next.cpus);
                  setMemoryGb(next.memoryGb);
                }}
                catalog={resourceCatalogQuery.data}
                isLoading={resourceCatalogQuery.isLoading}
              />
              <div className="field-hint">
                Exact pairs (RunPod <code>cpu3*/cpu5*</code>) pin both
                vCPU and RAM; range shapes (<code>8+</code>) let
                SkyPilot pick on AWS / GCP / Azure. Solid chips are
                providers enabled on this pool&rsquo;s inheritance
                chain; dashed chips are catalog-available but not
                enabled.
              </div>
            </div>
            <ResourceMismatchHint cpus={cpus} memoryGb={memoryGb} />
          </>
        )}
        <label>nodes per replica</label>
        <div className="field">
          <input
            type="number"
            min={1}
            value={numNodes}
            onChange={(e) => setNumNodes(Number(e.target.value))}
            style={{ width: 120 }}
          />
          <div className="field-hint">
            Multi-node only matters for huge models that can&rsquo;t fit
            on a single host. Default 1.
          </div>
        </div>
        <label>disk (GiB)</label>
        <div className="field">
          <input
            value={diskGb}
            onChange={(e) => setDiskGb(e.target.value)}
            placeholder="(optional)"
            style={{ width: 120 }}
          />
        </div>
        <label>placements</label>
        <div className="field">
          <PlacementsPreview placements={resolvedPlacements} />
          <div className="field-hint">
            Derived from the Provider config inheritance chain
            (Global / Service / Pool override). To change which clouds
            or regions this pool may use, edit the{" "}
            <strong>Providers</strong> tab.
          </div>
        </div>
      </div>

      {validationError != null && (
        <div className="flash" style={{ marginTop: 12 }}>
          <span className="mono">{validationError}</span>
        </div>
      )}
      {save.isError && (
        <div className="flash" style={{ marginTop: 12 }}>
          failed to save:{" "}
          <span className="mono">
            {(save.error as Error).message}
          </span>
        </div>
      )}
    </Section>
  );
}

// Per-pool workload override has moved to the dedicated **Workload**
// tab. The toggle there flips the pool between *inherit* (follow
// Service.workload) and *own* (full pool-scoped WorkloadSpec with its
// own draft / promote cycle) — see `PoolWorkload.tsx`. The previous
// in-line OverrideEditor / ArgsOverride / EnvOverride / sanitizeOverride
// helpers are gone.

/**
 * Inline preview of the controller's renderer-side preflight rule
 * (`controller/skypilot/renderer.py::validate_pool_resources`). Warns
 * the operator *before* save when the entered combination is the
 * known "exact CPU + range memory" trap that produces 0/0 replicas
 * with no obvious signal — most commonly because RunPod's
 * cpu3c-N-M family pins memory to a fixed N:M ratio when cpus is
 * exact. The save still goes through (the server is the source of
 * truth and will reject the same combination at provisioning time),
 * but the inline hint lets the operator fix it without waiting for
 * a reconcile tick to surface the failure on the Replicas tab.
 */
function ResourceMismatchHint({
  cpus,
  memoryGb,
}: {
  cpus: string;
  memoryGb: string;
}) {
  const cpusTrim = cpus.trim();
  const memTrim = memoryGb.trim();
  if (!cpusTrim || cpusTrim.endsWith("+")) return null;
  if (!memTrim || !memTrim.endsWith("+")) return null;
  return (
    <>
      <div />
      <div
        className="flash"
        style={{
          gridColumn: "2 / -1",
          marginTop: 4,
          fontSize: 12,
        }}
      >
        Heads-up: <code>cpus={cpusTrim}</code> demands exactly{" "}
        {cpusTrim} vCPU but <code>memory={memTrim}</code> is a range.
        On clouds with a fixed CPU:RAM ratio (e.g. RunPod{" "}
        <code>cpu3c-N-M</code>) this combination has no candidate and
        the controller will refuse to provision — change cpus to{" "}
        <code>{cpusTrim}+</code> or pin memory to an exact GiB value.
      </div>
    </>
  );
}

/**
 * Compact summary for the read-only kv block at the top of the spec
 * page. Single placement: "AWS · ap-northeast-1". Multi-placement: a
 * comma-separated list with each entry rendered "Cloud (region)".
 *
 * Operates over `ResolvedPlacement` rather than the now-removed
 * `PoolPlacement` — same on-the-wire shape, different provenance.
 */
function describePlacements(
  placements: ReadonlyArray<ResolvedPlacement> | undefined,
): string {
  if (!placements || placements.length === 0) return "(no providers enabled)";
  if (placements.length === 1) {
    const p = placements[0];
    const cloud = humanCloudLabel(p.source_cloud);
    return p.region ? `${cloud} · ${p.region}` : cloud;
  }
  return placements
    .map((p) => {
      const cloud = humanCloudLabel(p.source_cloud);
      return p.region ? `${cloud} (${p.region})` : `${cloud} (any)`;
    })
    .join(", ");
}

/**
 * Read-only chip list rendered under the "placements" label in the
 * spec form. Mirrors the shape of the old `PlacementsEditor` so the
 * page layout doesn't shift; the editor itself is gone because the
 * source of truth is the Providers tab now.
 */
function PlacementsPreview({
  placements,
}: {
  placements: ReadonlyArray<ResolvedPlacement>;
}) {
  if (placements.length === 0) {
    return (
      <div className="muted" style={{ fontSize: 12 }}>
        No providers are enabled in this pool&rsquo;s inheritance
        chain. Replicas will not be provisioned until at least one
        provider is enabled (Global / Service / Pool override).
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {placements.map((p, idx) => (
        <span
          key={`${p.source_cloud}-${p.region ?? "any"}-${idx}`}
          className="chip mono"
          style={{
            padding: "2px 8px",
            borderRadius: 4,
            background: "var(--chip-bg, rgba(255,255,255,0.05))",
            fontSize: 12,
          }}
        >
          {humanCloudLabel(p.source_cloud)}
          {p.region ? ` · ${p.region}` : " · any"}
        </span>
      ))}
    </div>
  );
}

interface ResourceShapeView {
  kind: ResourceKind;
  accelerators: string;
  cpus: string;
  memoryGb: string;
  numNodes: number;
}

function describeResource(v: ResourceShapeView): string {
  if (v.kind === "gpu") {
    const acc = v.accelerators.trim();
    if (acc === "") return "(cloud default shape)";
    const nodes = v.numNodes > 1 ? ` × ${v.numNodes} nodes` : "";
    return `${acc}${nodes}`;
  }
  const cpus = v.cpus.trim();
  if (cpus === "") return "(cloud default shape)";
  const mem = v.memoryGb.trim() !== "" ? ` / ${v.memoryGb.trim()} GiB RAM` : "";
  return `cpu ${cpus}${mem}`;
}

function isResourceDirty(pool: Pool, v: ResourceShapeView): boolean {
  const saved: ResourceShapeView = {
    kind: resourceKindOf(pool),
    accelerators: pool.accelerators ?? "",
    cpus: pool.cpus ?? "",
    memoryGb: pool.memory_gb ?? "",
    numNodes: pool.num_nodes,
  };
  return describeResource(saved) !== describeResource(v);
}
